/**
 * Chat helper utilities for building multimodal messages
 */

import { ContentBlock } from '../apis/backend';
import { AttachedImage } from '../components/ChatPage/ChatInput/ImageThumbnail';

/**
 * Build multimodal content blocks from text and images
 * Returns either a simple string (for text-only) or ContentBlock[] (for multimodal)
 */
export const buildMessageContent = (text: string, images: AttachedImage[]): string | ContentBlock[] => {
  // If no images, return simple text (backward compatible)
  if (images.length === 0) {
    return text;
  }

  // Build content blocks array
  const contentBlocks: ContentBlock[] = [];

  // Add text block if present
  if (text.trim()) {
    contentBlocks.push({
      type: 'text',
      text: text.trim(),
    });
  }

  // Add image blocks
  images.forEach((img) => {
    contentBlocks.push({
      type: 'image_url',
      image_url: {
        url: img.dataUrl,
        detail: 'high', // Use high detail for better analysis
      },
    });
  });

  return contentBlocks;
};

/**
 * Extract text content from multimodal message for display
 */
export const extractTextFromContent = (content: string | ContentBlock[]): string => {
  if (typeof content === 'string') {
    return content;
  }

  // Extract text from content blocks with fallback for missing text property
  const textBlocks = content.filter((block) => block.type === 'text').map((block) => block.text || '');

  return textBlocks.join(' ');
};

/**
 * Check if message has images
 */
export const hasImages = (content: string | ContentBlock[]): boolean => {
  if (typeof content === 'string') {
    return false;
  }

  return content.some((block) => block.type === 'image_url');
};

/**
 * Get image count from message
 */
export const getImageCount = (content: string | ContentBlock[]): number => {
  if (typeof content === 'string') {
    return 0;
  }

  return content.filter((block) => block.type === 'image_url').length;
};

/**
 * Chat message interface with multimodal support
 */
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string | ContentBlock[];
  timestamp?: string;
  collectionCards?: any;
  toolOutputs?: any[];
  images?: AttachedImage[]; // Store original images for persistence
}

/**
 * API message format for chat completions
 */
export interface ApiMessage {
  role: 'user' | 'assistant';
  content: string | ContentBlock[];
}

/**
 * Build API messages with smart image history management
 *
 * Phase 1 & 2 Implementation:
 * - Preserves images in conversation history (fixes forgetting issue)
 * - Implements cost optimization via detail level downgrade
 *
 * Strategy:
 * - Most recent image: detail='high' (full quality, baseline cost)
 * - 1-2 turns ago: detail='low' (66% cost reduction, ~150KB)
 * - Older than maxImageTurns: drop images entirely
 *
 * @param messages - Full chat history with images stored on ChatMessage.images
 * @param maxImageTurns - How many recent user turns to keep images (default: 2)
 * @returns API-formatted messages with optimized image retention
 *
 * Example:
 *   Turn 1 (user): Upload image A → detail='high'
 *   Turn 2 (user): "What about this?" + image B → A='low', B='high'
 *   Turn 3 (user): "And this?" + image C → A=dropped, B='low', C='high'
 */
export function buildApiMessagesWithImages(messages: ChatMessage[], maxImageTurns = 2): ApiMessage[] {
  const apiMessages: ApiMessage[] = [];
  let userTurnCount = 0;

  // Process messages in reverse to count user turns from most recent
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];

    // Count user turns (for image retention policy)
    if (msg.role === 'user') {
      userTurnCount++;
    }

    // Determine image handling for this message
    const hasStoredImages = msg.role === 'user' && msg.images && msg.images.length > 0;
    const isWithinTurnLimit = userTurnCount <= maxImageTurns;
    const isMostRecent = userTurnCount === 1;

    if (hasStoredImages && isWithinTurnLimit) {
      // Reconstruct multimodal content with appropriate detail level
      const contentBlocks: ContentBlock[] = [];

      // Extract text from content (handles both string and ContentBlock[])
      const textContent = extractTextFromContent(msg.content);
      if (textContent.trim()) {
        contentBlocks.push({ type: 'text', text: textContent.trim() });
      }

      // Add images with cost-optimized detail level
      msg.images!.forEach((img) => {
        contentBlocks.push({
          type: 'image_url',
          image_url: {
            url: img.dataUrl,
            detail: isMostRecent ? 'high' : 'low', // Phase 2: Cost optimization
          },
        });
      });

      apiMessages.unshift({
        role: msg.role,
        content: contentBlocks,
      });
    } else {
      // No images, outside turn limit, or assistant message → text only
      const textContent = extractTextFromContent(msg.content);
      apiMessages.unshift({
        role: msg.role,
        content: textContent,
      });
    }
  }

  return apiMessages;
}

/**
 * Build API messages with hybrid tool output strategy for cost optimization
 *
 * Strategy:
 * - Last 3 message pairs (6 messages): Full content + tool outputs
 * - Older messages: Text response + tool name summary only
 * - Images: Handled by buildApiMessagesWithImages logic
 * - No truncation: Full history preserved
 *
 * This approach:
 * - Keeps tool outputs visible in UI (localStorage)
 * - Reduces token usage ~60-70% by summarizing old tool outputs
 * - Maintains enough context to avoid redundant tool calls
 * - Allows unlimited conversation growth
 *
 * @param messages - Full chat history with tool outputs
 * @param recentPairCount - Number of recent message pairs to send with full tool outputs (default: 3)
 * @returns API-formatted messages with hybrid tool output handling
 */
export function buildApiMessagesWithHybridToolOutputs(messages: ChatMessage[], recentPairCount = 3): ApiMessage[] {
  // First apply image optimization
  const messagesWithOptimizedImages = buildApiMessagesWithImages(messages);

  // Calculate how many recent messages to keep with full tool outputs
  // Each pair = 1 user + 1 assistant message = 2 messages
  const recentMessageCount = recentPairCount * 2;
  let startIndexForRecent = Math.max(0, messagesWithOptimizedImages.length - recentMessageCount);

  // Ensure we don't split a user/assistant pair by aligning to user message boundaries
  // If startIndexForRecent lands on an assistant message, move it back to include the user message
  if (startIndexForRecent > 0 && messagesWithOptimizedImages[startIndexForRecent]?.role === 'assistant') {
    startIndexForRecent = Math.max(0, startIndexForRecent - 1);
  }

  // Process each message
  return messagesWithOptimizedImages.map((msg, idx) => {
    // Keep user messages as-is (they don't have tool outputs)
    if (msg.role === 'user') {
      return msg;
    }

    // For assistant messages, check if we should summarize tool outputs
    const isRecent = idx >= startIndexForRecent;

    // Safe assumption: buildApiMessagesWithImages preserves message order and count
    const originalMessage = messages[idx];
    if (!originalMessage) {
      return msg; // Fallback if index mismatch (defensive)
    }

    // Recent messages: keep full content (already optimized by buildApiMessagesWithImages)
    if (isRecent || !originalMessage.toolOutputs || originalMessage.toolOutputs.length === 0) {
      return msg;
    }

    // Older messages: summarize tool outputs
    const toolNames = originalMessage.toolOutputs.map((t) => t.tool_name || 'unknown').join(', ');
    const textContent = extractTextFromContent(msg.content);
    const summarizedContent = `[Used tools: ${toolNames}]\n${textContent}`;

    return {
      role: msg.role,
      content: summarizedContent,
    };
  });
}
